import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';

import { SessionStore } from '../../../core/auth/session.store';
import { UserRole } from '../../../core/auth/auth-session.model';
import { CreateUserRequest, LocationDto, TenantDto, UserDto } from '../models/user-admin.models';
import { UserAdminApi } from '../services/user-admin.api';

type UserViewMode = 'list' | 'tiles';

const USER_VIEW_STORAGE_KEY = 'junoaudit.users.view-mode';

@Component({
  selector: 'app-user-admin',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './user-admin.component.html',
  styleUrl: './user-admin.component.css',
})
export class UserAdminComponent {
  private readonly api = inject(UserAdminApi);
  private readonly fb = inject(FormBuilder);
  private readonly sessionStore = inject(SessionStore);
  private schemaManuallyEdited = false;
  private userEditRequestId = 0;
  private businessAdminReplacementRequestId: string | null = null;

  readonly isActive = input(false);
  readonly isCreating = input(false);
  readonly listViewRequest = input(0);
  readonly searchQuery = input('');
  readonly mode = input<'tenant-provisioning' | 'tenant-users'>('tenant-users');
  readonly creationClosed = output<void>();

  protected readonly isLoading = signal(false);
  protected readonly isLoadingUserEdit = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly userStatusPendingCode = signal<string | null>(null);
  protected readonly message = signal('');
  protected readonly error = signal('');
  protected readonly users = signal<UserDto[]>([]);
  protected readonly tenants = signal<TenantDto[]>([]);
  protected readonly viewMode = signal<UserViewMode>(this.restoreViewMode());
  protected readonly tenantPendingEdit = signal<TenantDto | null>(null);
  protected readonly userPendingEdit = signal<UserDto | null>(null);
  protected readonly isUserPasswordVisible = signal(false);
  protected readonly locations = signal<LocationDto[]>([]);
  protected readonly selectedLocationIds = signal<Set<string>>(new Set());
  protected readonly isLocationDialogOpen = signal(false);
  protected readonly isCreatingLocation = signal(false);
  protected readonly tenantById = computed(() => {
    const map = new Map<string, TenantDto>();
    for (const tenant of this.tenants()) {
      map.set(tenant.id, tenant);
    }
    return map;
  });
  protected readonly visibleUsers = computed(() => {
    const query = this.searchQuery().trim().toLocaleLowerCase('pl');
    if (!query) {
      return this.users();
    }

    return this.users().filter((user) =>
      [user.first_name, user.last_name, user.email, user.role]
        .join(' ')
        .toLocaleLowerCase('pl')
        .includes(query),
    );
  });
  protected readonly editedTenantUsers = computed(() => {
    const tenant = this.tenantPendingEdit();
    if (!tenant) {
      return [];
    }

    return this.users().filter((user) => user.tenant_id === tenant.id);
  });
  protected readonly editedTenantUserLimitReached = computed(() => {
    const tenant = this.tenantPendingEdit();
    if (!tenant?.user_limit) {
      return false;
    }

    return this.editedTenantUsers().length >= tenant.user_limit;
  });
  protected readonly editedTenantActiveBusinessAdmins = computed(() => {
    const tenant = this.tenantPendingEdit();
    return tenant
      ? this.users().filter(user => user.tenant_id === tenant.id && user.role === 'BUSINESS_ADMIN' && user.is_active)
      : [];
  });

  protected readonly provisioningForm = this.fb.nonNullable.group({
    tenant_name: ['', [Validators.required]],
    tenant_schema: ['', [Validators.required, Validators.maxLength(63), Validators.pattern(/^[a-z][a-z0-9_]*$/)]],
    service: ['BHP' as const, [Validators.required]],
    user_limit: [10, [Validators.required, Validators.min(1)]],
    admin_email: ['', [Validators.required, Validators.email]],
    admin_password: ['', [Validators.required, strongPasswordValidator]],
    admin_first_name: ['', [Validators.required]],
    admin_last_name: ['', [Validators.required]],
  });

  protected readonly userForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, strongPasswordValidator]],
    first_name: ['', [Validators.required]],
    last_name: ['', [Validators.required]],
    role: ['AUDITOR' as Exclude<UserRole, 'SUPER_ADMIN'>, [Validators.required]],
    is_active: [true],
  });

  protected readonly tenantEditForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    user_limit: [1, [Validators.required, Validators.min(1)]],
    is_active: [true],
  });

  protected readonly tenantUserForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, strongPasswordValidator]],
    first_name: ['', [Validators.required]],
    last_name: ['', [Validators.required]],
    role: ['AUDITOR' as Exclude<UserRole, 'SUPER_ADMIN'>, [Validators.required]],
    is_active: [true],
  });

  protected readonly businessAdminReplacementForm = this.fb.nonNullable.group({
    previous_admin_user_code: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, strongPasswordValidator]],
    first_name: ['', [Validators.required]],
    last_name: ['', [Validators.required]],
    must_change_password: [true],
    confirm: [false, [Validators.requiredTrue]],
  });

  protected readonly userEditForm = this.fb.nonNullable.group({
    first_name: ['', [Validators.required]],
    last_name: ['', [Validators.required]],
    role: ['AUDITOR' as Exclude<UserRole, 'SUPER_ADMIN'>, [Validators.required]],
    is_active: [true],
    password: ['', [strongPasswordValidator]],
    must_change_password: [true],
  });

  protected readonly locationForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
  });

  constructor() {
    this.provisioningForm.controls.tenant_name.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((tenantName) => {
        if (this.schemaManuallyEdited) {
          return;
        }

        this.provisioningForm.controls.tenant_schema.setValue(toTenantSchema(tenantName), {
          emitEvent: false,
        });
      });

    this.provisioningForm.controls.tenant_schema.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.schemaManuallyEdited = true;
      });

    effect(() => {
      if (this.isActive()) {
        void this.load();
      }
    });

    effect(() => {
      const listViewRequest = this.listViewRequest();
      if (listViewRequest > 0) {
        this.cancelUserEdit();
      }
    });
  }

  async load(): Promise<void> {
    this.isLoading.set(true);
    this.error.set('');

    try {
      const [users, tenants] = await Promise.all([
        firstValueFrom(this.api.listUsers()),
        this.mode() === 'tenant-provisioning'
          ? firstValueFrom(this.api.listTenants())
          : Promise.resolve([]),
      ]);
      this.users.set(users);
      this.tenants.set(tenants);
      this.refreshEditedTenant(tenants);
      this.refreshEditedUser();
    } catch {
      this.error.set('Nie udało się pobrać danych administracyjnych.');
    } finally {
      this.isLoading.set(false);
    }
  }

  protected async submitProvisioning(): Promise<void> {
    if (this.provisioningForm.invalid || this.isSaving()) {
      this.provisioningForm.markAllAsTouched();
      this.error.set(this.provisioningInvalidMessage());
      return;
    }

    this.isSaving.set(true);
    this.message.set('');
    this.error.set('');

    try {
      const formValue = this.provisioningForm.getRawValue();
      await firstValueFrom(
        this.api.provisionBhpTenant(formValue.tenant_name, formValue.tenant_schema),
      );
      const provisionedTenant = await firstValueFrom(this.api.provisionTenant(formValue));
      this.tenants.update((tenants) => [...tenants, provisionedTenant.tenant]);
      this.users.update((users) => [...users, provisionedTenant.admin_user]);
      this.message.set('Tenant i administrator klienta zostali utworzeni.');
      this.schemaManuallyEdited = false;
      this.provisioningForm.reset({
        tenant_name: '',
        tenant_schema: '',
        service: 'BHP',
        user_limit: 10,
        admin_email: '',
        admin_password: '',
        admin_first_name: '',
        admin_last_name: '',
      }, { emitEvent: false });
    } catch (error) {
      this.error.set(
        this.formatApiError(error, 'Nie udało się utworzyć tenanta albo administratora.'),
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  protected isProvisioningFieldInvalid(fieldName: string): boolean {
    const control = this.provisioningForm.get(fieldName);
    return Boolean(control?.invalid && control.touched);
  }

  protected provisioningFieldError(fieldName: string): string {
    const control = this.provisioningForm.get(fieldName);
    if (!control || !control.invalid || !control.touched) {
      return '';
    }

    if (control.hasError('required')) {
      return 'To pole jest wymagane.';
    }

    if (fieldName === 'tenant_schema' && control.hasError('pattern')) {
      return 'Użyj wyłącznie małych liter, cyfr i znaku _. Schema musi zaczynać się od litery.';
    }

    if (fieldName === 'tenant_schema' && control.hasError('maxlength')) {
      return 'Schema może mieć maksymalnie 63 znaki.';
    }

    if (fieldName === 'user_limit' && control.hasError('min')) {
      return 'Limit użytkowników musi wynosić co najmniej 1.';
    }

    if (fieldName === 'admin_email' && control.hasError('email')) {
      return 'Wpisz poprawny adres e-mail, np. admin@firma.pl.';
    }

    if (fieldName === 'admin_password' && control.hasError('strongPassword')) {
      return 'Hasło musi mieć min. 7 znaków oraz małą i dużą literę, cyfrę i znak specjalny.';
    }

    return 'Wpisana wartość jest niepoprawna.';
  }

  protected editTenant(tenant: TenantDto): void {
    this.tenantPendingEdit.set(tenant);
    this.tenantEditForm.reset({
      name: tenant.name,
      user_limit: tenant.user_limit ?? 1,
      is_active: tenant.is_active,
    });
    this.resetTenantUserForm();
    this.resetBusinessAdminReplacementForm(tenant.id);
    this.cancelUserEdit();
    this.message.set('');
    this.error.set('');
  }

  protected cancelTenantEdit(): void {
    this.tenantPendingEdit.set(null);
    this.tenantEditForm.reset({
      name: '',
      user_limit: 1,
      is_active: true,
    });
    this.resetTenantUserForm();
    this.businessAdminReplacementRequestId = null;
    this.cancelUserEdit();
  }

  protected async submitTenantEdit(): Promise<void> {
    const tenant = this.tenantPendingEdit();
    if (!tenant || this.tenantEditForm.invalid || this.isSaving()) {
      this.tenantEditForm.markAllAsTouched();
      this.error.set('Uzupełnij wymagane pola poprawnymi wartościami.');
      return;
    }

    this.isSaving.set(true);
    this.message.set('');
    this.error.set('');

    try {
      const updatedTenant = await firstValueFrom(
        this.api.updateTenant(tenant.id, this.tenantEditForm.getRawValue()),
      );
      this.replaceTenant(updatedTenant);
      this.message.set('Dane klienta zostały zapisane.');
      this.cancelTenantEdit();
    } catch (error) {
      this.error.set(this.formatApiError(error, 'Nie udało się zapisać danych klienta.'));
    } finally {
      this.isSaving.set(false);
    }
  }

  protected async toggleTenantActive(tenant: TenantDto): Promise<void> {
    if (this.isSaving()) {
      return;
    }

    this.isSaving.set(true);
    this.message.set('');
    this.error.set('');

    try {
      const updatedTenant = await firstValueFrom(
        this.api.updateTenant(tenant.id, {
          name: tenant.name,
          user_limit: tenant.user_limit ?? 1,
          is_active: !tenant.is_active,
        }),
      );
      this.replaceTenant(updatedTenant);
      this.message.set(
        tenant.is_active ? 'Klient został dezaktywowany.' : 'Klient został aktywowany.',
      );
    } catch (error) {
      this.error.set(this.formatApiError(error, 'Nie udało się zmienić statusu klienta.'));
    } finally {
      this.isSaving.set(false);
    }
  }

  protected async submitTenantUser(): Promise<void> {
    const tenant = this.tenantPendingEdit();
    if (!tenant) {
      return;
    }

    if (this.editedTenantUserLimitReached()) {
      this.error.set('Limit użytkowników dla tego tenanta został osiągnięty.');
      return;
    }

    if (this.tenantUserForm.invalid || this.isSaving()) {
      this.tenantUserForm.markAllAsTouched();
      this.error.set(this.invalidFormMessage(this.tenantUserForm.get('password')));
      return;
    }

    this.isSaving.set(true);
    this.message.set('');
    this.error.set('');

    try {
      const payload: CreateUserRequest = {
        ...this.tenantUserForm.getRawValue(),
        tenant_id: tenant.id,
      };
      const createdUser = await firstValueFrom(this.api.createUser(payload));
      this.users.update((users) => [...users, createdUser]);
      this.message.set('Użytkownik tenanta został utworzony.');
      this.resetTenantUserForm();
      this.cancelUserEdit();
    } catch (error) {
      this.error.set(this.formatApiError(error, 'Nie udało się utworzyć użytkownika tenanta.'));
    } finally {
      this.isSaving.set(false);
    }
  }

  protected async replaceBusinessAdmin(): Promise<void> {
    const tenant = this.tenantPendingEdit();
    if (!tenant || this.businessAdminReplacementForm.invalid || this.isSaving()) {
      this.businessAdminReplacementForm.markAllAsTouched();
      this.error.set('Uzupełnij dane nowego administratora i potwierdź zmianę.');
      return;
    }

    this.isSaving.set(true);
    this.message.set('');
    this.error.set('');
    this.businessAdminReplacementRequestId ??= createRequestId();
    const { confirm: _confirm, ...formValue } = this.businessAdminReplacementForm.getRawValue();
    try {
      const result = await firstValueFrom(this.api.replaceBusinessAdmin(tenant.id, {
        ...formValue,
        request_id: this.businessAdminReplacementRequestId,
      }));
      this.users.update(users => [
        ...users
          .filter(user => user.user_code !== result.new_admin.user_code)
          .map(user => user.tenant_id === tenant.id && user.role === 'BUSINESS_ADMIN'
            ? { ...user, is_active: false }
            : user),
        result.new_admin,
      ]);
      this.message.set(`Administrator został zmieniony. Przepisane audyty: ${result.updated_audits}.`);
      this.resetBusinessAdminReplacementForm(tenant.id);
    } catch (error) {
      this.error.set(this.formatApiError(
        error,
        'Nie udało się zmienić administratora. Poprzedni administrator pozostaje aktywny; możesz ponowić operację.',
      ));
    } finally {
      this.isSaving.set(false);
    }
  }

  protected async submitUser(): Promise<void> {
    if (this.userForm.invalid || this.isSaving()) {
      this.userForm.markAllAsTouched();
      this.error.set(this.invalidFormMessage(this.userForm.get('password')));
      return;
    }

    const session = this.sessionStore.snapshot();
    if (!session) {
      this.error.set('Brak aktywnej sesji.');
      return;
    }

    this.isSaving.set(true);
    this.message.set('');
    this.error.set('');

    try {
      const payload: CreateUserRequest = {
        ...this.userForm.getRawValue(),
        tenant_id: session.tenantId,
      };
      const createdUser = await firstValueFrom(this.api.createUser(payload));
      this.users.update((users) => [...users, createdUser]);
      this.message.set('Użytkownik został utworzony.');
      this.userForm.reset({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'AUDITOR',
        is_active: true,
      });
      this.creationClosed.emit();
    } catch (error) {
      this.error.set(
        this.formatApiError(
          error,
          'Nie udało się utworzyć użytkownika. Sprawdź limit lub poprawność danych.',
        ),
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  protected closeUserCreator(): void {
    if (this.isSaving()) {
      return;
    }

    this.userForm.reset({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'AUDITOR',
      is_active: true,
    });
    this.error.set('');
    this.creationClosed.emit();
  }

  protected async editUser(user: UserDto): Promise<void> {
    if (this.isLoadingUserEdit() || this.isSaving() || this.userStatusPendingCode()) {
      return;
    }

    const requestId = ++this.userEditRequestId;
    this.userPendingEdit.set(user);
    this.isUserPasswordVisible.set(false);
    this.populateUserEditForm(user);
    this.message.set('');
    this.error.set('');

    this.isLoadingUserEdit.set(true);
    try {
      const [freshUser, locationCatalog, userLocations] = await Promise.all([
        firstValueFrom(this.api.getUser(user.user_code)),
        this.mode() === 'tenant-users'
          ? firstValueFrom(this.api.listLocations())
          : Promise.resolve({ results: [] as LocationDto[] }),
        this.mode() === 'tenant-users'
          ? firstValueFrom(this.api.getUserLocations(user.user_code))
          : Promise.resolve({ user_code: user.user_code, locations: [] as LocationDto[] }),
      ]);
      if (requestId !== this.userEditRequestId) {
        return;
      }
      this.userPendingEdit.set(freshUser);
      this.locations.set(locationCatalog.results);
      this.selectedLocationIds.set(new Set(userLocations.locations.map(location => location.id)));
      this.populateUserEditForm(freshUser);
    } catch (error) {
      if (requestId !== this.userEditRequestId) {
        return;
      }
      this.userPendingEdit.set(null);
      this.resetUserEditForm();
      this.error.set(
        this.formatApiError(error, 'Nie udało się pobrać danych użytkownika do edycji.'),
      );
    } finally {
      if (requestId === this.userEditRequestId) {
        this.isLoadingUserEdit.set(false);
      }
    }
  }

  protected cancelUserEdit(): void {
    this.userEditRequestId += 1;
    this.isLoadingUserEdit.set(false);
    this.userPendingEdit.set(null);
    this.isUserPasswordVisible.set(false);
    this.locations.set([]);
    this.selectedLocationIds.set(new Set());
    this.closeLocationDialog();
    this.resetUserEditForm();
  }

  protected async submitUserEdit(): Promise<void> {
    const user = this.userPendingEdit();
    if (!user || this.userEditForm.invalid || this.isLoadingUserEdit() || this.isSaving()) {
      this.userEditForm.markAllAsTouched();
      this.error.set(
        this.userEditForm.controls.password.hasError('strongPassword')
          ? 'Hasło musi mieć co najmniej 7 znaków i zawierać małą literę, dużą literę, cyfrę oraz znak specjalny.'
          : 'Uzupełnij wymagane pola poprawnymi wartościami.',
      );
      return;
    }

    this.isSaving.set(true);
    this.message.set('');
    this.error.set('');

    try {
      const { password, must_change_password, ...profile } = this.userEditForm.getRawValue();
      const updatedUser = await firstValueFrom(
        this.api.updateUser(user.user_code, profile),
      );
      if (this.mode() === 'tenant-users') {
        await firstValueFrom(
          this.api.assignUserLocations(user.user_code, [...this.selectedLocationIds()]),
        );
      }
      if (password) {
        const passwordResult = await firstValueFrom(
          this.api.setUserPassword(user.user_code, { password, must_change_password }),
        );
        updatedUser.must_change_password = passwordResult.must_change_password;
      }
      this.replaceUser(updatedUser);
      this.message.set('Dane użytkownika zostały zapisane.');
      this.cancelUserEdit();
    } catch (error) {
      this.error.set(this.formatApiError(error, 'Nie udało się zapisać danych użytkownika.'));
    } finally {
      this.isSaving.set(false);
    }
  }

  protected async toggleUserActive(user: UserDto): Promise<void> {
    if (this.isSaving() || this.userStatusPendingCode()) {
      return;
    }

    this.message.set('');
    this.error.set('');
    this.userStatusPendingCode.set(user.user_code);

    try {
      const updatedUser = await firstValueFrom(
        this.api.updateUser(user.user_code, { is_active: !user.is_active }),
      );
      this.replaceUser(updatedUser);
    } catch (error) {
      this.error.set(this.formatApiError(error, 'Nie udało się zmienić statusu użytkownika.'));
    } finally {
      this.userStatusPendingCode.set(null);
    }
  }

  protected tenantName(tenantId: string): string {
    return this.tenantById().get(tenantId)?.name ?? tenantId;
  }

  protected canManageUser(user: UserDto): boolean {
    return user.role !== 'BUSINESS_ADMIN';
  }

  protected toggleUserPasswordVisibility(): void {
    this.isUserPasswordVisible.update((visible) => !visible);
  }

  protected isLocationSelected(locationId: string): boolean {
    return this.selectedLocationIds().has(locationId);
  }

  protected toggleLocation(locationId: string, selected: boolean): void {
    this.selectedLocationIds.update(current => {
      const next = new Set(current);
      selected ? next.add(locationId) : next.delete(locationId);
      return next;
    });
  }

  protected openLocationDialog(): void {
    this.locationForm.reset({ name: '' });
    this.isLocationDialogOpen.set(true);
  }

  protected closeLocationDialog(): void {
    if (this.isCreatingLocation()) return;
    this.isLocationDialogOpen.set(false);
    this.locationForm.reset({ name: '' });
  }

  protected async submitLocation(): Promise<void> {
    const name = this.locationForm.getRawValue().name.trim();
    if (this.locationForm.invalid || !name || this.isCreatingLocation()) {
      this.locationForm.markAllAsTouched();
      if (!name) this.locationForm.controls.name.setErrors({ required: true });
      return;
    }
    this.isCreatingLocation.set(true);
    this.error.set('');
    try {
      const location = await firstValueFrom(
        this.api.createLocation(name),
      );
      this.locations.update(locations => [...locations, location]);
      this.toggleLocation(location.id, true);
      this.isLocationDialogOpen.set(false);
      this.locationForm.reset({ name: '' });
    } catch (error) {
      this.error.set(this.formatApiError(error, 'Nie udało się utworzyć lokalizacji.'));
    } finally {
      this.isCreatingLocation.set(false);
    }
  }

  protected setViewMode(mode: UserViewMode): void {
    if (this.viewMode() === mode) {
      return;
    }

    this.viewMode.set(mode);
    localStorage.setItem(USER_VIEW_STORAGE_KEY, mode);
  }

  private refreshEditedTenant(tenants: TenantDto[]): void {
    const editedTenant = this.tenantPendingEdit();
    if (!editedTenant) {
      return;
    }

    const freshTenant = tenants.find((tenant) => tenant.id === editedTenant.id) ?? null;
    this.tenantPendingEdit.set(freshTenant);
    if (freshTenant) {
      this.tenantEditForm.reset({
        name: freshTenant.name,
        user_limit: freshTenant.user_limit ?? 1,
        is_active: freshTenant.is_active,
      });
    }
    this.refreshEditedUser();
  }

  private replaceTenant(updatedTenant: TenantDto): void {
    this.tenants.update((tenants) =>
      tenants.map((tenant) => (tenant.id === updatedTenant.id ? updatedTenant : tenant)),
    );
  }

  private replaceUser(updatedUser: UserDto): void {
    this.users.update((users) =>
      users.map((user) => (user.user_code === updatedUser.user_code ? updatedUser : user)),
    );
  }

  private resetTenantUserForm(): void {
    this.tenantUserForm.reset({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'AUDITOR',
      is_active: true,
    });
  }

  private resetBusinessAdminReplacementForm(tenantId: string): void {
    const currentAdmin = this.users().find(user =>
      user.tenant_id === tenantId && user.role === 'BUSINESS_ADMIN' && user.is_active,
    );
    this.businessAdminReplacementRequestId = null;
    this.businessAdminReplacementForm.reset({
      previous_admin_user_code: currentAdmin?.user_code ?? '',
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      must_change_password: true,
      confirm: false,
    });
  }

  private refreshEditedUser(): void {
    const editedUser = this.userPendingEdit();
    if (!editedUser) {
      return;
    }

    const freshUser = this.users().find((user) => user.user_code === editedUser.user_code) ?? null;
    this.userPendingEdit.set(freshUser);
    if (freshUser) {
      this.populateUserEditForm(freshUser);
    }
  }

  private populateUserEditForm(user: UserDto): void {
    this.userEditForm.reset({
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role === 'SUPER_ADMIN' ? 'AUDITOR' : user.role,
      is_active: user.is_active,
      password: '',
      must_change_password: true,
    });
  }

  private resetUserEditForm(): void {
    this.userEditForm.reset({
      first_name: '',
      last_name: '',
      role: 'AUDITOR',
      is_active: true,
      password: '',
      must_change_password: true,
    });
  }

  private restoreViewMode(): UserViewMode {
    return localStorage.getItem(USER_VIEW_STORAGE_KEY) === 'tiles' ? 'tiles' : 'list';
  }

  private formatApiError(error: unknown, fallback: string): string {
    if (!(error instanceof HttpErrorResponse)) {
      return fallback;
    }

    const payload = error.error;
    if (payload?.detail) {
      return `${fallback} ${payload.detail}`;
    }
    if (payload?.code) {
      return `${fallback} Kod: ${payload.code}.`;
    }
    if (payload && typeof payload === 'object') {
      const fieldErrors = Object.entries(payload)
        .flatMap(([field, value]) => {
          const messages = Array.isArray(value) ? value : [value];
          return messages.map((message) => `${field}: ${message}`);
        })
        .join(' ');

      if (fieldErrors) {
        return `${fallback} ${fieldErrors}`;
      }
    }

    return `${fallback} HTTP ${error.status}.`;
  }

  private invalidFormMessage(passwordControl: AbstractControl | null): string {
    if (passwordControl?.hasError('strongPassword')) {
      return 'Hasło musi mieć min. 7 znaków i zawierać małą, dużą literę, cyfrę oraz znak specjalny.';
    }

    return 'Uzupełnij wymagane pola poprawnymi wartościami.';
  }

  private provisioningInvalidMessage(): string {
    const fields: Array<[string, string]> = [
      ['tenant_name', 'Nazwa klienta'],
      ['tenant_schema', 'Schema'],
      ['service', 'Serwis'],
      ['user_limit', 'Limit użytkowników'],
      ['admin_email', 'Email admina'],
      ['admin_password', 'Hasło startowe'],
      ['admin_first_name', 'Imię admina'],
      ['admin_last_name', 'Nazwisko admina'],
    ];
    const invalidFields = fields
      .filter(([fieldName]) => this.provisioningForm.get(fieldName)?.invalid)
      .map(([, label]) => label);

    return invalidFields.length
      ? `Popraw pola: ${invalidFields.join(', ')}. Szczegóły są widoczne pod polami.`
      : 'Uzupełnij wymagane pola poprawnymi wartościami.';
  }
}

function strongPasswordValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '');
  if (!value) {
    return null;
  }

  const hasLower = [...value].some(
    (char) => char.toLowerCase() === char && char.toUpperCase() !== char,
  );
  const hasUpper = [...value].some(
    (char) => char.toUpperCase() === char && char.toLowerCase() !== char,
  );
  const hasDigit = [...value].some((char) => /\d/.test(char));
  const hasSpecial = [...value].some((char) => !/[A-Za-z0-9]/.test(char));

  return value.length >= 7 && hasLower && hasUpper && hasDigit && hasSpecial
    ? null
    : { strongPassword: true };
}

function createRequestId(): string {
  return globalThis.crypto.randomUUID();
}

function toTenantSchema(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .slice(0, 63)
    .replace(/_+$/g, '');

  if (!normalized || /^[a-z]/.test(normalized)) {
    return normalized;
  }

  return `tenant_${normalized}`.slice(0, 63).replace(/_+$/g, '');
}
